import { useTranslations } from "next-intl";
import { useSoftBounceTooltip } from "@/hooks/useSoftBounceTooltip";
import { useEmailSkipReasonLabel } from "@/hooks/useEmailSkipReasonLabel";
import {
  IconClockExclamation,
  IconMailOff,
  IconPaperclip,
} from "@tabler/icons-react";
import { EmailStatusInput } from "../../Inputs/EmailStatusInput";
import { useMemo } from "react";

type TableCellContentEmailStatusProps = {
  value: string;
  options: string[];
  onChangeValue: (value: string) => void;
  isFilter?: boolean;
  border?: string;
  height?: string;
  surface?: string;
  fontSize?: string;
  rounded?: string;
  padding?: string;
  error?: string | null;
  onFocusAction?: () => void;
  viewOnly?: boolean;
  /** Raw skip cause — one of the consumer codes mirrored in
   *  `lib/utils/emailSkipReason.ts` (the list lives there). On a SKIPPED badge it becomes the pill's
   *  tooltip AND a small mail-off chip beside it (the chip is the visible cue
   *  that there is something to hover). Ignored for every other status. */
  skipReason?: string | null;
  /** True when the email went out WITHOUT some/all attachments — renders a
   *  visible warning chip next to the status pill (a tooltip alone would be
   *  undiscoverable on an otherwise-green SENT/DELIVERED row). */
  attachmentsDegraded?: boolean | null;
  /**
   * Every time SES refused this message TEMPORARILY.
   *
   * Deliberately NOT folded into `value`. A transient bounce usually lands on a
   * row that is already delivered, and the recipient goes on to open and click
   * it — the status has to be free to say so. This renders as a small amber
   * mark beside the pill, so "it arrived" and "it did not go smoothly" can both
   * be true, which is what the row actually records.
   */
  softBounces?: { at?: string; subType?: string }[] | null;
};

export const TableCellContentEmailStatus: React.FC<
  TableCellContentEmailStatusProps
> = ({
  onChangeValue,
  onFocusAction,
  value,
  options,
  isFilter,
  border,
  height,
  surface,
  fontSize,
  rounded,
  padding,
  error,
  viewOnly,
  skipReason,
  attachmentsDegraded,
  softBounces,
}) => {
  const t = useTranslations("optionsMultiSingleSelect");
  const tDrop = useTranslations("attachmentDropReason");
  const skipReasonLabel = useEmailSkipReasonLabel();

  // Localized tooltip for a SKIPPED badge. The shared hook knows every
  // consumer code in `lib/utils/emailSkipReason.ts` and words an unknown one
  // generically — this used to map two and hand the rest back RAW ("undeliverable" as a tooltip).
  // Empty for any non-skipped status.
  const skipTooltip = useMemo(
    () => (value === "SKIPPED" ? skipReasonLabel(skipReason) : undefined),
    [value, skipReason, skipReasonLabel],
  );

  const softBounceTooltip = useSoftBounceTooltip(softBounces);

  // Create a proper Map with original options as keys and translated/original values as values
  const optionsMap = useMemo(() => {
    const map = new Map<string, string>();
    options?.forEach((option) => {
      const displayValue = t(option);
      map.set(option, displayValue);
    });
    return map;
  }, [options, t]);

  // Convert the Map values to an array for the OptionInput
  const displayOptions = useMemo(() => {
    return Array.from(optionsMap.values());
  }, [optionsMap]);

  // Find the display value for the current value
  const currentDisplayValue = useMemo(() => {
    return optionsMap.get(value) || "";
  }, [optionsMap, value]);

  // Function to get the original key from a display value
  const getKeyFromDisplayValue = (displayValue: string): string => {
    for (const [key, val] of optionsMap.entries()) {
      if (val === displayValue) {
        return key;
      }
    }
    return displayValue; // Fallback to the display value if no key is found
  };

  if (isFilter) {
    return (
      <div className="flex flex-col flex-1">
        <EmailStatusInput
          disabled={viewOnly}
          initialValue={currentDisplayValue}
          height={height}
          surface={surface}
          border={border}
          fontSize={fontSize}
          padding={padding}
          rounded={rounded}
          onFocusAction={onFocusAction}
          setParent={(selectedDisplayValue) => {
            // Convert the selected display value back to the original key
            const originalKey = getKeyFromDisplayValue(selectedDisplayValue);
            onChangeValue(originalKey);
          }}
          options={displayOptions}
        />
        {error && <p className="mt-1 body-3-regular opacity-0">{error}</p>}
      </div>
    );
  }

  // No email status yet (guest never received a send) → render an empty
  // cell instead of an empty bordered pill, which looked like a broken
  // placeholder. Filter mode above still renders its picker.
  if (!value) {
    return <div className="w-full" />;
  }

  return (
    <div className="w-full flex items-center gap-1">
      <p
        title={skipTooltip}
        className={`${fontSize}
    w-max px-3 py-1.5 border rounded-xl flex items-center
    ${
      value === "SENT" || value === "SUCCESS"
        ? "user-icon-font-2 outline-user-icon-2 user-icon-bg-2" // Blue - informational
        : value === "DELIVERED"
          ? "user-icon-font-4 outline-user-icon-4 user-icon-bg-4" // Green - success
          : value === "BOUNCED"
            ? "user-icon-font-3 outline-user-icon-3 user-icon-bg-3" // Red - error
            : value === "COMPLAINED"
              ? "user-icon-font-12 outline-user-icon-12 user-icon-bg-12" // Dark red/pink - serious issue
              : value === "REJECTED"
                ? "user-icon-font-3 outline-user-icon-3 user-icon-bg-3" // Red - error
                : value === "FAILED"
                  ? "user-icon-font-12 outline-user-icon-12 user-icon-bg-12" // Dark red/pink - serious issue
                  : value === "DELAYED" || value === "SOFT_BOUNCED"
                    ? "user-icon-font-11 outline-user-icon-11 user-icon-bg-11" // Yellow/orange - warning (a deferral, not a rejection)
                    : value === "PENDING"
                      ? "user-icon-font-9 outline-user-icon-9 user-icon-bg-9" // Light blue - in progress
                      : value === "SKIPPED"
                        ? "user-icon-font-17 outline-user-icon-17 user-icon-bg-17" // Muted grey-green - intentionally not sent
                        : value === "OPEN"
                        ? "user-icon-font-7 outline-user-icon-7 user-icon-bg-7" // Light green - positive action
                        : value === "CLICK"
                          ? "user-icon-font-10 outline-user-icon-10 user-icon-bg-10" // Purple - engagement/action
                          : ""
    }`}
      >
        {t(value)}
      </p>
      {/* Beside the pill, same family as the two marks below: a bare `title`
          on a grey pill is a tooltip nobody knows to look for. */}
      {/* `title` is the hover tooltip; `role="img"` + `aria-label` give the
          icon-only marks a name a screen reader can read — a bare svg has
          none, and Tabler icons carry no aria of their own. */}
      {skipTooltip && (
        <span
          title={skipTooltip}
          role="img"
          aria-label={skipTooltip}
          data-testid="skip-reason-chip"
          // A theme token, not the pill's `user-icon-font-17`: that colour has
          // no dark override and the chip no background — 1.43:1 on the dark
          // cell, i.e. invisible.
          className="shrink-0 flex items-center font-tertiary"
        >
          <IconMailOff size={14} stroke={2} aria-hidden="true" />
        </span>
      )}
      {softBounceTooltip && (
        <span
          title={softBounceTooltip}
          role="img"
          aria-label={softBounceTooltip}
          data-testid="soft-bounce-chip"
          className="shrink-0 flex items-center user-icon-font-11"
        >
          <IconClockExclamation size={14} stroke={2} aria-hidden="true" />
        </span>
      )}
      {attachmentsDegraded === true && (
        <span
          title={tDrop("degradedTooltip")}
          role="img"
          aria-label={tDrop("degradedTooltip")}
          data-testid="attachments-degraded-chip"
          className="shrink-0 flex items-center font-error"
        >
          <IconPaperclip size={14} stroke={2} aria-hidden="true" />
        </span>
      )}
    </div>
  );
};
